/**
 * Governed task context as a data-free shared recipe (#926 / #1466, ADR F-30).
 *
 * The host supplies already-governed evidence and the finite actions its source
 * door says are legal. This component fetches nothing, knows no module and does
 * not infer authority from labels, personas or AI advice. The callback must call
 * the owning domain's ordinary governed verb; MCP and UI therefore share the
 * same authority boundary.
 */
import { useEffect, useMemo, useState } from 'react'
import { WORK_ACTION_KEYS, type WorkActionKey } from '@solum/experience'
import { readableError } from '@solum/format'
import { Badge, Button, Input, Surface } from '../controls'
import type { ButtonProps } from '../controls'
import s from './WorkContext.module.css'

export { WORK_ACTION_KEYS }
export type { WorkActionKey }
export type ApprovalWorkAction = Extract<WorkActionKey, 'approve' | 'reject' | 'request_changes'>

export interface WorkActionInput {
  kind: 'reason'
  required: boolean
  label: string
  placeholder?: string
  submitLabel: string
}
export interface WorkAction {
  key: WorkActionKey
  /** Human copy belongs to the governed context/locale, never a component map. */
  label: string
  /** The source capability this affordance invokes; descriptive, not authority. */
  capability: string
  emphasis?: NonNullable<ButtonProps['variant']>
  input?: WorkActionInput
}
export interface WorkContextField { key: string; label: string; value: unknown }
export interface WorkContextSection { key: string; label: string; fields: WorkContextField[] }
export interface WorkContextData {
  instanceId: string
  taskState: string
  kindLabel?: string
  title: string
  summary?: string | null
  whyNow?: { step?: string | null; requestedAt?: string | null; dueAt?: string | null; overdue?: boolean }
  sections: WorkContextSection[]
  recommendation?: { kind?: string; label?: string; body?: string } | null
  canAct: boolean
  blocker?: string | null
  actions: WorkAction[]
  receipt?: { verdict: string; reason?: string | null; actedAt: string; locale?: string; revision?: string } | null
  locale: string
  revision: string
}

/** The current approvals door returns verdict keys; this is its explicit UI adapter. */
export type ApprovalWorkContextData = Omit<WorkContextData, 'actions'> & {
  actions: ApprovalWorkAction[]
}

const approvalActions: Record<ApprovalWorkAction, WorkAction> = {
  approve: {
    key: 'approve', label: 'Approve', capability: 'approvals.act', emphasis: 'primary',
  },
  request_changes: {
    key: 'request_changes', label: 'Request changes', capability: 'approvals.act', emphasis: 'secondary',
    input: { kind: 'reason', required: true, label: 'Changes needed',
      placeholder: 'What needs to change?', submitLabel: 'Send change request' },
  },
  reject: {
    key: 'reject', label: 'Decline', capability: 'approvals.act', emphasis: 'ghost',
    input: { kind: 'reason', required: true, label: 'Reason for decline',
      placeholder: 'Why are you declining?', submitLabel: 'Confirm decline' },
  },
}

export function isApprovalWorkAction(value: WorkActionKey): value is ApprovalWorkAction {
  return value === 'approve' || value === 'reject' || value === 'request_changes'
}

export function adaptApprovalWorkContext(context: ApprovalWorkContextData): WorkContextData {
  return {
    ...context,
    kindLabel: context.kindLabel ?? 'Decision workspace',
    actions: context.actions.filter(isApprovalWorkAction).map((key) => approvalActions[key]),
  }
}

const knownAction = new Set<string>(WORK_ACTION_KEYS)
const display = (value: unknown) => {
  if (value == null || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function WorkContext({ context, onAction }: {
  context: WorkContextData
  onAction?: (action: WorkActionKey, reason?: string) => Promise<void> | void
}) {
  // Runtime filtering is the finite-grammar boundary for definitions arriving
  // as JSON. An unknown key does not become a button merely because it was data.
  const actions = useMemo(() => context.actions.filter((action) =>
    knownAction.has(action.key) && action.label.trim() && action.capability.trim()), [context.actions])
  const [intent, setIntent] = useState<WorkAction | null>(null)
  const [reason, setReason] = useState('')
  const [busyAction, setBusyAction] = useState<WorkActionKey | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (intent && !actions.some((action) => action.key === intent.key)) {
      setIntent(null); setReason('')
    }
  }, [actions, intent])

  const act = async (action: WorkAction) => {
    setBusyAction(action.key); setError(null)
    try {
      await onAction?.(action.key, reason.trim() || undefined)
      setIntent(null); setReason('')
    } catch (e) {
      setError(readableError(e, 'The action could not be recorded.'))
    } finally { setBusyAction(null) }
  }
  const choose = (action: WorkAction) => {
    if (action.input) { setIntent(action); setReason(''); setError(null) }
    else void act(action)
  }

  return (
    <div className={s.wrap} data-testid="work-context" data-revision={context.revision} lang={context.locale}>
      <header className={s.head}>
        <div><div className={s.eyebrow}>{context.kindLabel ?? 'Work item'}</div><h2>{context.title}</h2></div>
        <Badge tone={context.whyNow?.overdue ? 'critical' : context.taskState === 'running' ? 'warn' : 'neutral'}>
          {context.whyNow?.overdue ? 'overdue' : context.taskState.replaceAll('_', ' ')}
        </Badge>
      </header>
      {context.summary && <p className={s.summary}>{context.summary}</p>}
      <div className={s.why}>
        {context.whyNow?.step && <span><b>Why now</b> · {context.whyNow.step.replaceAll('_', ' ')}</span>}
        {context.whyNow?.dueAt && <span><b>Due</b> · {new Date(context.whyNow.dueAt).toLocaleString(context.locale)}</span>}
      </div>

      {context.sections.map((section) => (
        <section key={section.key} className={s.section}>
          <h3>{section.label}</h3>
          <dl>{section.fields.map((field) => (
            <div key={field.key}><dt>{field.label}</dt><dd>{display(field.value)}</dd></div>
          ))}</dl>
        </section>
      ))}

      {context.recommendation && (
        <Surface inset className={s.recommendation} data-testid="work-recommendation">
          <b>{context.recommendation.label || 'Recommendation'}</b>
          <p>{context.recommendation.body}</p>
          <small>Advisory only · authority and the final action stay with you.</small>
        </Surface>
      )}

      {context.blocker && <p role="status" className={s.blocker}>Cannot act: {context.blocker.replaceAll('_', ' ')}</p>}
      {context.receipt && (
        <Surface inset className={s.receipt} data-testid="work-receipt">
          <b>Recorded: {context.receipt.verdict.replaceAll('_', ' ')}</b>
          {context.receipt.reason && <p>{context.receipt.reason}</p>}
          <small>{new Date(context.receipt.actedAt).toLocaleString(context.locale)} · evidence {context.receipt.revision}</small>
        </Surface>
      )}

      {context.canAct && onAction && actions.length > 0 && (
        <section className={s.actions} aria-label="Work actions">
          {intent?.input ? (
            <>
              <Input aria-label={intent.input.label} value={reason} disabled={busyAction !== null}
                onChange={(e) => setReason(e.target.value)} placeholder={intent.input.placeholder} />
              <Button variant={intent.emphasis} loading={busyAction === intent.key}
                disabled={busyAction !== null || (intent.input.required && !reason.trim())}
                onClick={() => void act(intent)}>
                {intent.input.submitLabel}
              </Button>
              <Button variant="ghost" disabled={busyAction !== null}
                onClick={() => { setIntent(null); setReason('') }}>Cancel</Button>
            </>
          ) : actions.map((action) => (
            <Button key={action.key} variant={action.emphasis ?? 'secondary'}
              loading={busyAction === action.key} disabled={busyAction !== null}
              data-capability={action.capability} onClick={() => choose(action)}>{action.label}</Button>
          ))}
        </section>
      )}
      {error && <p role="alert" className={s.error}>{error}</p>}
    </div>
  )
}
